import React from 'react';
import styled from 'styled-components';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Select, TextField } from 'Components/ui';

/* ── Styled Components ── */

const FormCard = styled.div`
  max-width: 720px;
  background: white;
  border-radius: 14px;
  padding: 32px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 28px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--vo-cloud);
`;

const IconBox = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: linear-gradient(135deg, var(--vo-cobalto), var(--vo-capri));
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const SectionTitle = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: var(--vo-ink);
  margin: 0;
`;

const SectionSubtitle = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: var(--vo-slate);
  margin: 0;
`;

const FieldsWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FieldLabel = styled.label`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: var(--vo-ink-2);
`;

const ErrorText = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: var(--vo-danger);
  margin: 0;
`;

/* ── Component ── */

export const Details = ({
  register,
  control,
  errors,
  handleComplete,
  handleSubmit,
  usersGroups = [],
}) => {
  const { t } = useTranslation(['policy']);

  return (
    <FormCard>
      {/* Card section header */}
      <CardHeader>
        <IconBox>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
          </svg>
        </IconBox>
        <div>
          <SectionTitle>{t('policy:form.sectionTitle')}</SectionTitle>
          <SectionSubtitle>{t('policy:form.sectionSubtitle')}</SectionSubtitle>
        </div>
      </CardHeader>

      {/* Fields */}
      <form id="policy-form" onSubmit={handleSubmit(handleComplete)}>
        <FieldsWrap>

          {/* Nome */}
          <Field>
            <FieldLabel htmlFor="policy-name">
              {t('policy:form.name.label')} *
            </FieldLabel>
            <TextField
              id="policy-name"
              fullWidth
              error={!!errors?.name}
              placeholder={t('policy:form.name.placeholder', 'Ex: Política de Privacidade')}
              autoFocus
              {...register('name', { required: t('policy:form.name.required') })}
            />
            {errors?.name && <ErrorText>{errors.name.message}</ErrorText>}
          </Field>

          {/* Classificação */}
          <Field>
            <FieldLabel htmlFor="policy-classification">
              {t('policy:form.classification.label')} *
            </FieldLabel>
            <TextField
              id="policy-classification"
              fullWidth
              error={!!errors?.classification}
              placeholder={t('policy:form.classification.placeholder', 'Ex: Política de Privacidade Externa')}
              {...register('classification', { required: t('policy:form.classification.required') })}
            />
            {errors?.classification && <ErrorText>{errors.classification.message}</ErrorText>}
          </Field>

          {/* Grupos de usuários */}
          <Field>
            <FieldLabel>{t('policy:form.userGroups.label')}</FieldLabel>
            <Controller
              control={control}
              name="userGroups"
              render={({ field: { onChange, value = [] }, fieldState }) => (
                <>
                  <Select
                    multiple
                    name="userGroups"
                    value={value}
                    onChange={onChange}
                    error={!!fieldState.error}
                    options={usersGroups.map(g => ({ value: g._id, label: g.name }))}
                  />
                  {fieldState.error && <ErrorText>{fieldState.error.message}</ErrorText>}
                </>
              )}
            />
          </Field>

          {/* Descrição */}
          <Field>
            <FieldLabel htmlFor="policy-description">
              {t('policy:form.description.label')} *
            </FieldLabel>
            <TextField
              id="policy-description"
              multiline
              fullWidth
              error={!!errors?.description}
              rows={4}
              placeholder={t('policy:form.description.placeholder', 'Descreva o objetivo e escopo desta política...')}
              {...register('description', { required: t('policy:form.description.required') })}
            />
            {errors?.description && <ErrorText>{errors.description.message}</ErrorText>}
          </Field>

        </FieldsWrap>
      </form>
    </FormCard>
  );
};
